import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import moment from 'moment';
import { db } from '../lib/firebase';
import '../styles/Transfer.css';
import progress from '../images/progress.png';
import { useNavigate } from 'react-router-dom';
import Verification from '../components/Verification';
import { sendTransactionEmail } from '../lib/Email'; // <-- added import

const Withdraw = ({ user, setInvestedBalance }) => {
    const [beneficiary, setBeneficiary] = useState('');
    const [bankName, setBankName] = useState('');
    const [bankAccountNumber, setBankAccountNumber] = useState('');
    const [walletAddress, setWalletAddress] = useState('');
    const [amount, setAmount] = useState('');
    const [description, setDescription] = useState('');
    const [date, setDate] = useState(moment().format('YYYY-MM-DD'));
    const [account, setAccount] = useState('Investments');
    const [accountBalances, setAccountBalances] = useState({});
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);
    const [userGroup, setUserGroup] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [userReport, setUserReport] = useState(null);
    const [isSuccess, setIsSuccess] = useState(false);
    const [transactionDetails, setTransactionDetails] = useState(null);
    const [showVerificationBox, setShowVerificationBox] = useState(false);
    const [wallets, setWallets] = useState({}); // ✅ Firestore wallets

    const navigate = useNavigate();

    // ✅ Fetch wallets once
    useEffect(() => {
        async function fetchWallets() {
            try {
                const snap = await getDoc(doc(db, "shipments", "bitworld"));
                if (snap.exists()) {
                    const data = snap.data();
                    const sorted = Object.entries(data)
                        .filter(([k]) => /^wallet\d+$/.test(k))
                        .sort(
                            (a, b) =>
                                parseInt(a[0].replace("wallet", ""), 10) -
                                parseInt(b[0].replace("wallet", ""), 10)
                        )
                        .reduce((obj, [k, w]) => {
                            obj[k] = { name: w.name, address: w.address };
                            return obj;
                        }, {});
                    setWallets(sorted);
                }
            } catch (err) {
                console.error("Error fetching wallets:", err);
            }
        }
        fetchWallets();
    }, []);

    useEffect(() => {
        if (user && user.uid) {
            const fetchUserData = async () => {
                try {
                    const userDoc = await getDoc(doc(db, 'bit', user.uid));
                    if (userDoc.exists()) {
                        const userData = userDoc.data();
                        const fetchedTransactions = userData.transactions || [];
                        const accountBalances = {};

                        const userGroup = userData.group || 'No group available';
                        const userRole = userData.role || 'No role available';
                        const userReport = userData.report || 'No report available';

                        const credits = fetchedTransactions.reduce((total, txn) =>
                            txn.type === 'credit' && txn.account === 'Investments' ? total + txn.amount : total, 0);
                        const debits = fetchedTransactions.reduce((total, txn) =>
                            txn.type === 'debit' && txn.account === 'Investments' ? total + txn.amount : total, 0);
                        const totalBalance = credits - debits;

                        accountBalances['Investments'] = totalBalance;

                        setAccountBalances(accountBalances);
                        setUserGroup(userGroup);
                        setUserRole(userRole);
                        setUserReport(userReport);
                    }
                } catch (error) {
                    console.error('Error fetching user data: ', error);
                }
            };

            fetchUserData();
        }
    }, [user, setInvestedBalance]);

    useEffect(() => {
        const parsedAmount = parseFloat(amount);
        const bankSelected = beneficiary === 'Bank';
        const cryptoSelected = beneficiary && beneficiary !== 'Bank';

        const validBank = bankSelected && bankName && bankAccountNumber;
        const validCrypto = cryptoSelected && walletAddress;

        const isValid =
            beneficiary &&
            (validBank || validCrypto) &&
            parsedAmount > 0 &&
            parsedAmount <= (accountBalances[account] || 0) &&
            description;

        setIsFormValid(isValid);
    }, [beneficiary, bankName, bankAccountNumber, walletAddress, amount, description, accountBalances, account]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormSubmitting(true);

        if (!isFormValid) {
            setFormSubmitting(false);
            return;
        }

        const parsedAmount = parseFloat(amount);
        const beneficiaryAccount =
            beneficiary === 'Bank' ? `${bankName} - ${bankAccountNumber}` : walletAddress;

        try {
            const transactionDescription = `${beneficiary} withdrawal to ${beneficiaryAccount}`;
            const transaction = {
                beneficiary,
                beneficiaryAccount,
                amount: parsedAmount,
                description: userGroup === 'deactivated'
                    ? `[Pending] ${beneficiary} withdrawal to ${beneficiaryAccount}`
                    : transactionDescription,
                date: moment(date).format('MMMM D, YYYY'),
                type: 'debit',
                account: 'Investments',
            };

            // write transaction to Firestore first
            await updateDoc(doc(db, 'bit', user.uid), {
                transactions: arrayUnion(transaction),
            });

            // if deactivated handle group change
            if (userGroup === 'deactivated') {
                await updateDoc(doc(db, 'bit', user.uid), { group: 'cancelled' });
                setUserGroup('cancelled');
            }

            // --- Trigger transactional email (non-blocking in sense errors won't break flow)
            try {
                // construct transaction payload including bankName for template
                const txForEmail = {
                    ...transaction,
                    bankName: bankName || '', // empty for crypto withdrawals
                };

                // user email and name fallbacks
                const recipientEmail = (user && (user.email || user.emailAddress)) || '';
                const userName = (user && (user.fullName || user.displayName || user.name)) || 'User';

                await sendTransactionEmail(
                    recipientEmail,
                    beneficiaryAccount,
                    userName,
                    txForEmail
                );
            } catch (emailErr) {
                // Log email error but don't interrupt the UX
                console.error('Error sending transaction email:', emailErr);
            }

            // set success state and reset form (same as before)
            setTransactionDetails(transaction);
            setIsSuccess(true);
            setBeneficiary('');
            setBankName('');
            setBankAccountNumber('');
            setWalletAddress('');
            setAmount('');
            setDescription('');
            setDate(moment().format('YYYY-MM-DD'));
            setAccount('Investments');
        } catch (error) {
            console.error('Error adding transaction: ', error);
        } finally {
            setFormSubmitting(false);
        }
    };

    if (isSuccess) {
        return (
            <div className="add-transactions">
                <div className='center'>
                    <img
                        src={progress}
                        alt={userGroup === 'deactivated' ? "Transaction Pending" : "Success"}
                        className="transferIMG"
                    />
                </div>
                <h2 className='center'>
                    {userGroup === 'deactivated' ? "Transaction is Pending!" : "Transaction was Successful!"}
                </h2>
                <div><strong>Network:</strong> {transactionDetails.beneficiary}</div>
                <div><strong>Account/Wallet:</strong> {transactionDetails.beneficiaryAccount}</div>
                <div><strong>Amount:</strong> {transactionDetails?.amount}</div>
                <div><strong>Date:</strong> {transactionDetails?.date}</div>
                <button
                    onClick={() => {
                        navigate('/node');
                        window.location.reload();
                    }}
                    className="click-button"
                >
                    Back to Dashboard
                </button>
            </div>
        );
    }

    return (
        <div className="add-transactions" id="transfer">
            <form onSubmit={handleSubmit}>
                <div className="form-cover">
                    <div>
                        <label className="transaction-head">Select Payment Method:</label>
                        <select
                            value={beneficiary}
                            className="input-field"
                            onChange={(e) => {
                                setBeneficiary(e.target.value);
                                const selected = Object.values(wallets).find(
                                    (w) => w.name === e.target.value
                                );
                                setWalletAddress(selected?.address || "");
                            }}
                            required
                        >
                            <option value="" disabled>Choose an option</option>
                            <option value="Bank">Bank Transfer</option>
                            {Object.values(wallets).map((w) => (
                                <option key={w.name} value={w.name}>
                                    {w.name.toUpperCase()}
                                </option>
                            ))}
                        </select>
                    </div>

                    {beneficiary === 'Bank' && (
                        <>
                            <div>
                                <label className="transaction-head">Bank Name:</label>
                                <input
                                    type="text"
                                    value={bankName}
                                    className="input-field"
                                    placeholder="e.g., Chase Bank"
                                    onChange={(e) => setBankName(e.target.value)}
                                    required
                                />
                            </div>
                            <div>
                                <label className="transaction-head">Account Number:</label>
                                <input
                                    type="text"
                                    value={bankAccountNumber}
                                    className="input-field"
                                    placeholder="e.g., 123456789"
                                    onChange={(e) => setBankAccountNumber(e.target.value)}
                                    required
                                />
                            </div>
                        </>
                    )}

                    {beneficiary && beneficiary !== 'Bank' && (
                        <div>
                            <label className="transaction-head">{beneficiary} Wallet Address:</label>
                            <input
                                type="text"
                                // value={walletAddress}
                                className="input-field"
                                placeholder={`e.g., ${walletAddress || "Enter your wallet"}`}
                                onChange={(e) => setWalletAddress(e.target.value)}
                                required
                            />
                        </div>
                    )}
                </div>

                {/* ✅ rest of your form unchanged */}
                <div className="form-cover">
                    <div>
                        <label className="transaction-head">Amount:</label>
                        <input
                            type="number"
                            value={amount}
                            className="input-field"
                            placeholder="e.g., 100"
                            onChange={(e) => setAmount(e.target.value)}
                            required
                            min="0.01"
                            step="0.01"
                        />
                        <div className='transaction-head'>
                            Available: ${accountBalances['Investments'] !== undefined
                                ? accountBalances['Investments'].toFixed(2)
                                : '0.00'}
                        </div>
                    </div>

                    <div>
                        <label className="transaction-head">Description:</label>
                        <input
                            type="text"
                            className="input-field"
                            value={description}
                            placeholder="e.g., First Withdrawal"
                            onChange={(e) => setDescription(e.target.value)}
                            required
                        />
                    </div>
                </div>

                <div>
                    {userGroup === 'deactivated' ? (
                        <>
                            <p style={{ color: "red", marginTop: "1rem" }}>{userReport}</p>
                            <button
                                className="verify-transactions-button"
                                onClick={() => setShowVerificationBox(true)}
                            >
                                Verify Transactions
                            </button>
                        </>
                    ) : userRole === 'investor' ? (
                        <p className="cancelled-text user-names">
                            Investment is still ongoing. You will be able to withdraw once eligible.
                        </p>
                    ) : (
                        <button
                            type="submit"
                            className={`delete-button ${formSubmitting || !isFormValid ? 'disabled' : ''}`}
                            disabled={formSubmitting || !isFormValid}
                        >
                            {formSubmitting ? 'Processing...' : 'Withdraw'}
                        </button>
                    )}

                    {showVerificationBox && (
                        <Verification setShowVerificationBox={setShowVerificationBox} />
                    )}
                </div>
            </form>
        </div>
    );
};

export default Withdraw;
